import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../layout/Navbar';
import PlanningHistoryCard from './cards/PlanningHistoryCard';
import PlanningFormCard from './cards/SalesOrderPlanningNew';
import PlanningBalanceCard from './cards/PlanningBalanceCard';
import Select from 'react-select';

import {
  SALES_ORDER_CUSTOMER_LIST,
  SALES_ORDER_BY_CUSTOMER,
  SALES_ORDER_MATERIALS,
  SALES_ORDER_PLAN_CREATE,
  SALES_ORDER_DESTINATIONS,
  SALES_ORDER_EXISTING_PLANS,
  WABA_PLANNING_TRIGGER
} from '../../config/api';

import { decryptData } from '../../utils/encrypt';

import SOInvoicedQtyCard from './cards/SOInvoicedQtyCard';
import { SALES_ORDER_INVOICE_SUMMARY } from '../../config/api';

function SalesOrderPlanningNew() {
  const [materialList, setMaterialList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [destinationList, setDestinationList] = useState([]);
  const [form, setForm] = useState({
    customer_id: '',
    sales_order_id: '',
    delivery_date: '',
    remark: ''
  });
  const [activePlanRow, setActivePlanRow] = useState(null);
  const [planDetails, setPlanDetails] = useState({});
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [existingPlans, setExistingPlans] = useState([]);

  const [invoiceSummary, setInvoiceSummary] = useState([]);

  const fetchInvoiceSummary = async (sales_order_id) => {
    try {
      const res = await fetch(SALES_ORDER_INVOICE_SUMMARY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sales_order_id })
      });
      const data = await res.json();
      if (Array.isArray(data)) setInvoiceSummary(data);
      else setInvoiceSummary([]);
    } catch (err) {
      console.error('Error fetching invoiced qty:', err);
      setInvoiceSummary([]);
    }
  };

  useEffect(() => {
    if (form.sales_order_id) {
      fetchMaterialsBySalesOrder(form.sales_order_id);
      fetchExistingPlans(form.sales_order_id);
      fetchInvoiceSummary(form.sales_order_id); // <-- keep invoice summary in sync
    }
  }, [form.sales_order_id]);

  useEffect(() => {
    const encrypted = localStorage.getItem('suyog_user');
    if (encrypted) {
      const parsed = decryptData(encrypted);
      setUserId(parsed.user_id);
      setOrgId(parsed?.selected_org?.value || null);
      fetchCustomers(parsed.user_id, parsed?.selected_org?.value || null);
    }
  }, []);

  const fetchCustomers = async (user_id, org_id) => {
    try {
      const res = await fetch(SALES_ORDER_CUSTOMER_LIST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, org_id })
      });
      const data = await res.json();
      if (Array.isArray(data)) setCustomerList(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchOrdersByCustomer = async (customer_id) => {
    setOrderList([]);
    setMaterialList([]);
    setDestinationList([]);
    setExistingPlans([]);
    setInvoiceSummary([]);
    setForm(prev => ({ ...prev, sales_order_id: '' }));

    try {
      const res = await fetch(SALES_ORDER_BY_CUSTOMER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id, org_id: orgId })
      });
      const data = await res.json();
      if (Array.isArray(data)) setOrderList(data);
    } catch (err) {
      console.error('Sales order fetch error:', err);
    }

    try {
      const destRes = await fetch(SALES_ORDER_DESTINATIONS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id })
      });
      const destData = await destRes.json();
      if (Array.isArray(destData)) setDestinationList(destData);
    } catch (err) {
      console.error('Error fetching destinations:', err);
    }
  };

  const fetchMaterialsBySalesOrder = async (sales_order_id) => {
    try {
      const res = await fetch(SALES_ORDER_MATERIALS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sales_order_id })
      });

      const data = await res.json();

      if (Array.isArray(data)) {
        const formatted = data.map(mat => ({
          ...mat,
          qty: parseFloat(mat.qty || 0),
          id: mat.material_id || mat.id,
          material_name: mat.material_name || ''
        }));
        setMaterialList(formatted);
      } else {
        setMaterialList([]);
      }
    } catch (err) {
      console.error('Error fetching materials:', err);
      setMaterialList([]);
    }
  };

  const fetchExistingPlans = async (sales_order_id) => {
    try {
      const res = await fetch(SALES_ORDER_EXISTING_PLANS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sales_order_id })
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setExistingPlans(data);
      } else {
        setExistingPlans([]);
      }
    } catch (err) {
      console.error('Error fetching existing plans:', err);
      setExistingPlans([]);
    }
  };

  // (kept) additional effect; harmless duplication in your current file
  useEffect(() => {
    if (form.sales_order_id) {
      fetchMaterialsBySalesOrder(form.sales_order_id);
      fetchExistingPlans(form.sales_order_id);
    }
  }, [form.sales_order_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'customer_id') {
      fetchOrdersByCustomer(value);
    }
  };

  const handlePlanningInput = (materialId, field, value) => {
    let updatedValue = value;
    if (field === 'planned_qty') {
      updatedValue = value.replace(/[^\d.]/g, '').replace(/(\.\d{3})\d*/, '$1');
    }
    setPlanDetails(prev => ({
      ...prev,
      [materialId]: {
        ...prev[materialId],
        [field]: updatedValue
      }
    }));
  };

  const handleSave = async () => {
    const payload = {
      user_id: userId,
      org_id: orgId,
      customer_id: form.customer_id,
      sales_order_id: form.sales_order_id,
      plans: Object.entries(planDetails).map(([material_id, details]) => ({
        material_id,
        planned_qty: details.planned_qty,
        delivery_date: details.delivery_date,
        destination: details.destination === '__manual' ? details.manual_destination : details.destination,
        priority: details.priority,
        remark: details.remark,
        packing_style: details.packing_style
      }))
    };

    try {
      const res = await fetch(SALES_ORDER_PLAN_CREATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (res.ok && data.success) {
        const insertedId = data.inserted_id;
        alert(`Plan saved successfully for ${data.success} item(s)`);

        try {
          const wabaRes = await fetch(WABA_PLANNING_TRIGGER, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan_id: insertedId })
          });

          const text = await wabaRes.text();
          if (text) {
            const json = JSON.parse(text);
            console.log('WABA response:', json);
          } else {
            console.warn('WABA trigger returned empty response');
          }
        } catch (wabaErr) {
          console.error('WABA trigger error:', wabaErr);
        }

        setActivePlanRow(null);
        setPlanDetails({});
        fetchExistingPlans(form.sales_order_id);
      } else {
        alert(`Failed to save plan. ${data.errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      alert('Server error while saving plan.');
    }
  };

  // 🔎 Typeable Customer options + selected object for react-select
  const customerOptions = useMemo(
    () =>
      (customerList || []).map(c => ({
        value: String(c.id),
        label: c.account_name
      })),
    [customerList]
  );

  const selectedCustomerOption = useMemo(
    () =>
      customerOptions.find(o => String(o.value) === String(form.customer_id || '')) || null,
    [customerOptions, form.customer_id]
  );

  return (
    <>
      <Navbar />

      <div className="container mt-4">
        <div className="card shadow-sm rounded-4 border">
          <div className="card-body">
            <h4 className="card-title mb-4 text-primary">Sales Order Planning</h4>

            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label">Customer</label>
                {/* ✅ react-select: type to search */}
                <Select
                  options={customerOptions}
                  value={selectedCustomerOption}
                  onChange={(opt) => {
                    const newId = opt ? String(opt.value) : '';
                    setForm(prev => ({ ...prev, customer_id: newId, sales_order_id: '' }));

                    if (newId) {
                      fetchOrdersByCustomer(newId);
                    } else {
                      setOrderList([]);
                      setMaterialList([]);
                      setDestinationList([]);
                      setExistingPlans([]);
                      setInvoiceSummary([]);
                    }
                  }}
                  isClearable
                  isSearchable
                  placeholder="-- Select Customer --"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-8">
                <label className="form-label">Sales Order</label>
                <select
                  name="sales_order_id"
                  value={form.sales_order_id}
                  onChange={handleChange}
                  className="form-select"
                  disabled={!orderList.length}
                >
                  <option value="">-- Select Sales Order --</option>
                  {orderList.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.po_string}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PlanningFormCard
              materialList={materialList}
              activePlanRow={activePlanRow}
              setActivePlanRow={setActivePlanRow}
              planDetails={planDetails}
              handlePlanningInput={handlePlanningInput}
              handleSave={handleSave}
              destinationList={destinationList}
              existingPlans={existingPlans}
            />

            <PlanningBalanceCard
              materialList={materialList}
              existingPlans={existingPlans}
              invoiceSummary={invoiceSummary}
            />

            <PlanningHistoryCard plans={existingPlans} />
          </div>
        </div>
      </div>
    </>
  );
}

export default SalesOrderPlanningNew;
